import { roundToCents } from '@dcp/shared'
import type { Cell } from 'exceljs'
import type { CashPositionReport, ReportColumn, ReportRow } from './cashPositionReport'
import {
  reportBlockRowStyles,
  reportHeaderStyle,
  reportSectionCaption,
  reportTopLine,
  reportValueCellStyle,
  type ReportCellStyle,
  type ReportRowStyle,
  type ReportTopLineCell,
} from './cashPositionReportStyle'

const SHEET_NAME = 'Cash Position Summary'
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
/** Whole dollars, negatives in parentheses like the screen, zero as a dash. */
const MONEY_FORMAT = '#,##0;(#,##0);"-"'
/** exceljs paper size code for US Legal (8.5" x 14"). */
const PAPER_LEGAL = 5
const MIN_LABEL_WIDTH = 30
const VALUE_WIDTH = 16

/** Excel's default size is 11; emphasised rows read one step larger, as in the PDF. */
const FONT_SIZE = 11
const EMPHASIS_FONT_SIZE = 12

/** One spreadsheet line of the report with the shared report style attached. */
export type SheetLine =
  | {
      kind: 'title'
      /** Caption, date (spanning `dateSpan` cells) and weekday, with empty cells under the span. */
      cells: (string | null)[]
      topLine: [ReportTopLineCell, ReportTopLineCell, ReportTopLineCell]
      dateSpan: number
    }
  | { kind: 'header'; cells: string[] }
  | { kind: 'section'; style: ReportRowStyle; cells: [string, ...(string | null)[]] }
  | { kind: Exclude<ReportRow['kind'], 'section'>; style: ReportRowStyle; cells: [string, ...(number | null)[]] }

/**
 * Lays the report out as spreadsheet lines: the as-of top line (caption, date
 * and weekday in their own cells, the date spanning as many columns as its
 * text needs), the column header (a pack subtotal reads "<pack>\nTotal", as on
 * screen), then every block's rows with their style; a section row carries
 * its caption in the combined and pack columns. Labels the style
 * upper-cases are upper-cased here so the text matches, not just the look.
 * Values are rounded to cents: the RPC's sums carry float noise
 * (767592.5700000001) that Excel would show in the formula bar.
 */
export function cashPositionSheetLines(report: CashPositionReport): SheetLine[] {
  const header = [
    '',
    ...report.columns.map((col) => {
      const label = reportHeaderStyle(col).upper ? col.label.toUpperCase() : col.label
      return col.kind === 'pack' ? `${label}\nTotal` : label
    }),
  ]
  const topLine = reportTopLine(report.asOf)
  const [caption, date, weekday] = topLine
  const dateSpan = Math.ceil((date.text.length + 2) / VALUE_WIDTH)
  const lines: SheetLine[] = [
    {
      kind: 'title',
      cells: [caption.text, date.text, ...Array<null>(dateSpan - 1).fill(null), weekday.text],
      topLine,
      dateSpan,
    },
    { kind: 'header', cells: header },
  ]
  for (const block of report.blocks) {
    const styles = reportBlockRowStyles(block)
    block.rows.forEach((row, i) => lines.push(toSheetLine(row, styles[i], report.columns)))
  }
  return lines
}

function toSheetLine(row: ReportRow, style: ReportRowStyle, columns: ReportColumn[]): SheetLine {
  const label = style.label.upper ? row.label.toUpperCase() : row.label
  if (row.kind === 'section') {
    return { kind: 'section', style, cells: [label, ...columns.map(reportSectionCaption)] }
  }
  return {
    kind: row.kind,
    style,
    cells: [label, ...row.values.map((v) => (v == null ? null : roundToCents(v)))],
  }
}

/** Applies a shared cell style with this writer's font sizes and exceljs ARGB colours. */
function applyStyle(cell: Cell, style: ReportCellStyle): void {
  cell.font = {
    bold: style.bold,
    italic: style.italic,
    size: style.emphasis ? EMPHASIS_FONT_SIZE : FONT_SIZE,
    color: { argb: argb(style.textColor) },
  }
  if (style.fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(style.fill) } }
}

/** '#RRGGBB' as exceljs ARGB, fully opaque. */
function argb(hex: string): string {
  return `FF${hex.slice(1)}`
}

/**
 * Renders the report as a one-sheet XLSX workbook. exceljs is loaded on demand —
 * it is large and only needed when someone downloads.
 */
export async function buildCashPositionXlsx(report: CashPositionReport): Promise<Blob> {
  const { Workbook } = await import('exceljs')
  const workbook = new Workbook()
  const lines = cashPositionSheetLines(report)
  const columnCount = report.columns.length + 1
  // 1-based row number of the column header, frozen and repeated on print.
  const headerRowNumber = lines.findIndex((l) => l.kind === 'header') + 1

  const sheet = workbook.addWorksheet(SHEET_NAME, {
    views: [{ state: 'frozen', xSplit: 1, ySplit: headerRowNumber }],
    pageSetup: {
      orientation: 'landscape',
      paperSize: PAPER_LEGAL,
      fitToPage: true,
      fitToWidth: 1,
      // 0 = as many pages tall as needed; only the width is squeezed.
      fitToHeight: 0,
      printTitlesRow: `${headerRowNumber}:${headerRowNumber}`,
      printTitlesColumn: 'A:A',
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 },
    },
  })

  for (const line of lines) {
    const row = sheet.addRow(line.cells)
    switch (line.kind) {
      case 'title': {
        const [caption, date, weekday] = line.topLine
        applyStyle(row.getCell(1), caption.style)
        applyStyle(row.getCell(2), date.style)
        // The date box spans enough value columns to hold the date, like the PDF's fitted box.
        if (line.dateSpan > 1) sheet.mergeCells(row.number, 2, row.number, line.dateSpan + 1)
        applyStyle(row.getCell(line.dateSpan + 2), weekday.style)
        break
      }
      case 'header':
        for (let col = 1; col <= columnCount; col++) {
          const cell = row.getCell(col)
          const style = reportHeaderStyle(col === 1 ? null : report.columns[col - 2])
          applyStyle(cell, style)
          cell.alignment = { vertical: 'bottom', horizontal: style.align, wrapText: true }
        }
        break
      case 'section':
        // Only the label cell carries the band, as in the PDF; the combined and
        // pack columns carry the caption.
        applyStyle(row.getCell(1), line.style.label)
        for (let col = 2; col <= columnCount; col++) {
          if (line.cells[col - 1] === null) continue
          const cell = row.getCell(col)
          applyStyle(cell, reportValueCellStyle(line.style, report.columns[col - 2]))
          cell.alignment = { horizontal: 'right' }
        }
        break
      case 'value':
      case 'total':
        applyStyle(row.getCell(1), line.style.label)
        for (let col = 2; col <= columnCount; col++) {
          const cell = row.getCell(col)
          cell.numFmt = MONEY_FORMAT
          applyStyle(cell, reportValueCellStyle(line.style, report.columns[col - 2]))
        }
        break
      default: {
        const unreachable: never = line
        throw new Error(`Unknown sheet line ${JSON.stringify(unreachable)}`)
      }
    }
  }

  const longestLabel = Math.max(
    0,
    ...lines.filter((l) => l.kind !== 'title' && l.kind !== 'header').map((l) => String(l.cells[0]).length),
  )
  sheet.getColumn(1).width = Math.max(MIN_LABEL_WIDTH, longestLabel + 2)
  for (let col = 2; col <= columnCount; col++) sheet.getColumn(col).width = VALUE_WIDTH

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: XLSX_MIME })
}
